<script>
  import colorable from '@/mixins/colorable';

  export default {
    name: 'ProgressCircular',
    mixins: [colorable],
    props: {
      button: {
        type: Boolean,
      },
      indeterminate: {
        type: Boolean,
      },
      rotate: {
        type: [Number, String],
        default: 0,
      },
      size: {
        type: [Number, String],
        default: 32,
      },
      width: {
        type: [Number, String],
        default: 4,
      },
      value: {
        type: [Number, String],
        default: 0,
      },
      color: {
        type: String,
        default: '#777',
      },
    },

    computed: {
      calculatedSize() {
        return Number(this.size) + (this.button ? 8 : 0);
      },

      circumference() {
        return 2 * Math.PI * this.radius;
      },

      classRoot() {
        return this.b({
          isIndeterminate: this.indeterminate,
          isButton: this.button,
        });
      },

      normalizedValue() {
        if (this.value < 0) {
          return 0;
        }

        if (this.value > 100) {
          return 100;
        }

        return parseFloat(this.value);
      },

      radius() {
        return 20;
      },

      strokeDashArray() {
        return Math.round(this.circumference * 1000) / 1000;
      },

      strokeDashOffset() {
        return `${((100 - this.normalizedValue) / 100) * this.circumference}px`;
      },

      strokeWidth() {
        return (Number(this.width) / +this.size) * this.viewBoxSize * 2;
      },

      styles() {
        return {
          height: `${this.calculatedSize}px`,
          width: `${this.calculatedSize}px`,
        };
      },

      svgStyles() {
        return {
          transform: `rotate(${Number(this.rotate)}deg)`,
        };
      },

      viewBoxSize() {
        return this.radius / (1 - Number(this.width) / +this.size);
      },
    },

    methods: {
      genCircle(h, name, offset) {
        return h('circle', {
          class: `progress-circular__${name}`,
          attrs: {
            fill: 'transparent',
            cx: 2 * this.viewBoxSize,
            cy: 2 * this.viewBoxSize,
            r: this.radius,
            'stroke-width': this.strokeWidth,
            'stroke-dasharray': this.strokeDashArray,
            'stroke-dashoffset': offset,
          },
        });
      },
      genSvg(h) {
        const children = [
          this.indeterminate || this.genCircle(h, 'underlay', 0),
          this.genCircle(h, 'overlay', this.strokeDashOffset),
        ];

        return h('svg', {
          style: this.svgStyles,
          attrs: {
            xmlns: 'http://www.w3.org/2000/svg',
            viewBox: `${this.viewBoxSize} ${this.viewBoxSize} ${2 * this.viewBoxSize} ${2 * this.viewBoxSize}`,
          },
        }, children);
      },
    },

    render(h) {
      const info = h('div', { staticClass: 'progress-circular__info' }, this.$slots.default);
      const svg = this.genSvg(h);

      return h('div', this.setTextColor(this.color, {
        staticClass: 'progress-circular',
        attrs: {
          role: 'progressbar',
          'aria-valuemin': 0,
          'aria-valuemax': 100,
          'aria-valuenow': this.indeterminate ? undefined : this.normalizedValue,
        },
        class: this.classRoot,
        style: this.styles,
        on: this.$listeners,
      }), [svg, info]);
    },
  };
</script>

<style lang="scss">
  $progressCircularRotateAnimation: progress-circular-rotate 1.4s linear infinite;
  $progressCircularRotateDash: progress-circular-dash 1.4s ease-in-out infinite;
  $processCircularIntermediateSvgTransition: all .2s ease-in-out;
  $progressCircularUnderlayStroke: rgba(#000, 0.1);
  $progressCircularOverlayTransition: all .6s ease-in-out;

  .progress-circular {
    position: relative;
    display: inline-flex;
    vertical-align: middle;

    svg {
      width: 100%;
      height: 100%;
      margin: auto;
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      z-index: 0;
    }

    &_is-indeterminate {
      svg {
        animation: $progressCircularRotateAnimation;
        transform-origin: center center;
        transition: $processCircularIntermediateSvgTransition;
      }

      .progress-circular__overlay {
        animation: $progressCircularRotateDash;
        stroke-linecap: round;
        stroke-dasharray: 80, 200;
        stroke-dashoffset: 0;
      }
    }

    &__underlay {
      stroke: $progressCircularUnderlayStroke;
      z-index: 1;
    }

    &__overlay {
      stroke: currentColor;
      z-index: 2;
      transition: $progressCircularOverlayTransition;
    }

    &__info {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }

    @keyframes progress-circular-dash {
      0% {
        stroke-dasharray: 1, 200;
        stroke-dashoffset: 0;
      }

      50% {
        stroke-dasharray: 100, 200;
        stroke-dashoffset: -15px;
      }

      100% {
        stroke-dasharray: 100, 200;
        stroke-dashoffset: -125px;
      }
    }

    @keyframes progress-circular-rotate {
      100% {
        transform: rotate(360deg);
      }
    }
  }
</style>
